import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { UserMenu } from "@/components/chrome/UserMenu";

function HamburgerIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M4 4L16 16M16 4L4 16"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3 6H17M3 10H17M3 14H17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Nav() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (toggleRef.current?.contains(t)) return;
      setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (mobileOpen && menuRef.current) {
      const first = menuRef.current.querySelector<HTMLElement>("a, button:not(:disabled)");
      first?.focus();
    }
  }, [mobileOpen]);

  function closeMenu() {
    setMobileOpen(false);
    toggleRef.current?.focus();
  }

  async function handleSignOut() {
    closeMenu();
    await signOut();
    await navigate({ to: "/" });
  }

  return (
    <header className="nav">
      <Link to="/" className="nav__brand" aria-label="home">
        <img className="mark" src="/logo-mark.svg" alt="" />
        <img className="wordmark" src="/logo-wordmark.svg" alt="AUTOMATONS.APP" />
      </Link>
      <nav className="nav__links">
        <Link to="/marketplace">MARKETPLACE</Link>
      </nav>
      <div className="nav__status">
        <span className="nav__heartbeat" aria-hidden="true" />
        <span className="nav__status-text">
          <b>1,247</b> alive · <b>8,392</b> stopped
        </span>
      </div>
      <div className="nav__auth">
        {user ? (
          <div className="nav__session" data-testid="nav-session">
            <Link
              to="/wallet"
              className="nav__credits"
              data-testid="nav-credits"
              data-zero={user.credits === 0 ? "true" : undefined}
              aria-label={`Wallet balance: ${user.credits} credits — open wallet`}
            >
              {user.credits}c
            </Link>
            <Link to="/spawn" className="cta cta--primary" data-testid="nav-cta-spawn-more">
              Spawn more
              <img
                className="cta__arrow"
                src="/icons/arrow-right.svg"
                alt=""
                style={{ filter: "brightness(0)" }}
              />
            </Link>
            <UserMenu />
          </div>
        ) : (
          <Link to="/login" className="cta cta--primary" data-testid="nav-cta-get-started">
            Get started
            <img
              className="cta__arrow"
              src="/icons/arrow-right.svg"
              alt=""
              style={{ filter: "brightness(0)" }}
            />
          </Link>
        )}
      </div>
      {/* Hamburger toggle — shown on mobile (≤560px) only via CSS */}
      <button
        ref={toggleRef}
        type="button"
        className="nav__mobile-toggle"
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
        aria-expanded={mobileOpen}
        aria-controls="nav-mobile-menu"
        data-testid="nav-mobile-toggle"
        onClick={() => setMobileOpen((o) => !o)}
      >
        <HamburgerIcon open={mobileOpen} />
      </button>
      {mobileOpen && (
        <div
          ref={menuRef}
          id="nav-mobile-menu"
          className="nav__mobile-menu"
          data-testid="nav-mobile-menu"
        >
          <Link
            to="/marketplace"
            className="nav__mobile-link"
            data-testid="nav-mobile-marketplace"
            onClick={closeMenu}
          >
            MARKETPLACE
          </Link>
          <div className="nav__mobile-status">
            <span className="nav__heartbeat" aria-hidden="true" />
            <span className="nav__status-text">
              <b>1,247</b> alive · <b>8,392</b> stopped
            </span>
          </div>
          <div className="nav__mobile-divider" />
          {user ? (
            <>
              <Link
                to="/wallet"
                className="nav__mobile-link"
                data-testid="nav-mobile-credits"
                onClick={closeMenu}
              >
                wallet · {user.credits}c
              </Link>
              <Link
                to="/spawn"
                className="nav__mobile-link"
                data-testid="nav-mobile-spawn"
                onClick={closeMenu}
              >
                spawn more
              </Link>
              <div className="nav__mobile-divider" />
              <div className="nav__mobile-eyebrow">{user.email}</div>
              <Link
                to="/library"
                className="nav__mobile-link"
                data-testid="nav-mobile-library"
                onClick={closeMenu}
              >
                library
              </Link>
              <button
                type="button"
                className="nav__mobile-link nav__mobile-link--button"
                data-testid="nav-mobile-signout"
                onClick={() => void handleSignOut()}
              >
                sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="nav__mobile-link"
              data-testid="nav-mobile-login"
              onClick={closeMenu}
            >
              get started
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
